import {
  type SendDirectPaymentResponse,
  TransactionStatus,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { useDirectPaymentMutation } from "Api/Mutations/Transactions/useDirectPaymentMutation";
import { usePISPAuthenticate } from "Api/Mutations/Transactions/usePISPAuthenticate";
import {
  type FixedAmount,
  PaymentDrawerForm,
  type PaymentSubmitData,
} from "Components/Chat/PaymentDrawerForm";
import { buildPaymentMessageText } from "Components/Chat/chatAttachmentTypes";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { useNavigate } from "Navigation/useNavigate";
import { isApiCurrencyCode } from "Utils/CurrencyUtils";
import {
  type ErrorType,
  getTransactionErrorMessage,
} from "Utils/TransactionErrorsUtils";
import { appUrl } from "Utils/UrlUtils";
import type { Channel } from "stream-chat";
import { toast } from "sonner";

const RESOURCES = Resources.Chat.DirectPayment.Drawer;
const TRANSACTION_ERRORS = Resources.TransactionErrors;
const PAYMENT_MENU_RESOURCES = Resources.Chat.PaymentMenu;

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channel: Channel;
  recipientIdentityUserPublicID: string;
  /**
   * Paying a multi-payer request: the amount and currency are the request's, and the payment
   * message is posted as a thread reply under the request message instead of in the timeline.
   */
  paymentRequest?: FixedAmount & { messageId: string };
};

/**
 * One-call direct payment (initiate on behalf of the recipient + pay) from inside the chat.
 * The bank handling mirrors the pay page (usePaymentSubmit / useTransactionPay): a signing URL
 * is followed, a server-authorized bank (CZKT) lands on the transaction detail, and a PISP-first
 * bank (mBank) is sent through the PISP authenticate endpoint — all AFTER the payment message is
 * posted, so the channel shows the payment while the bank flow is still in progress (the card
 * polls the transaction for the outcome).
 */
export const CreateDirectPaymentDrawer = ({
  isOpen,
  onClose,
  channel,
  recipientIdentityUserPublicID,
  paymentRequest,
}: Props) => {
  const { t } = useResource();
  const navigate = useNavigate();
  const { data: bankAccounts } = useBankingAccounts();

  const {
    mutateAsync: sendDirectPayment,
    isPending,
    error,
  } = useDirectPaymentMutation();

  const { mutate: fetchAndRedirectToPISPURL } = usePISPAuthenticate();

  const payError = error as ErrorType | null;
  const errorSubjects = payError?.data?.errors ?? {};
  // Funds land on the recipient's primary account (App ADR-0009): none set, or not Active.
  const isRecipientPrimaryAccountMissing = !!errorSubjects.recipientBankAccount;
  // The payment is denominated in the request currency — the backend rejects it when the sender
  // or the recipient account is denominated differently. Message-keyed (ResultErrors convention):
  // the subject is `accountInvalidCurrency`, not the request field.
  const isInvalidCurrency = !!errorSubjects.accountInvalidCurrency;
  // Pre-approval caps: an onboarding legal-entity recipient can only receive CZK within limits.
  const isRecipientCurrencyNotSupported =
    !!errorSubjects.currencyNotSupportedPreApproval;
  const isRecipientLimitExceeded =
    !!errorSubjects.oneTimeReceiveLimitExceededPreApproval ||
    !!errorSubjects.cumulativeReceiveLimitExceededPreApproval;
  // Paying yourself: the sender account is the recipient's account (PayTransactionCommand, ZS-1674).
  const isSameAccount = !!errorSubjects.debtorCreditorAccountIdentical;
  // The recipient and the payer must both be accepted members of this channel (membership gate).
  const isNotChannelMember = !!errorSubjects.channelId;
  // A Lite recipient (App ADR-0013) — the menu disables it upfront when the Stream profile
  // carries the tier; this is the fallback for a profile that does not.
  const isRecipientNotFullMoone = !!errorSubjects.recipientNotFullMoone;

  const getErrorContent = () => {
    if (isRecipientNotFullMoone) {
      return t(PAYMENT_MENU_RESOURCES.RecipientNotFullMoone);
    }
    if (isRecipientPrimaryAccountMissing) {
      return t(RESOURCES.RecipientNoPrimaryAccount);
    }
    if (isInvalidCurrency) {
      return t(RESOURCES.InvalidCurrency);
    }
    if (isRecipientCurrencyNotSupported) {
      return t(RESOURCES.RecipientCurrencyNotSupported);
    }
    if (isRecipientLimitExceeded) {
      return t(RESOURCES.RecipientLimitExceeded);
    }
    if (isSameAccount) {
      return t(TRANSACTION_ERRORS.DebtorCreditorAccountIdentical);
    }
    if (isNotChannelMember) {
      return t(RESOURCES.NotChannelMember);
    }
    // Everything the pay endpoint can say (limits, balance, bank availability...) is mapped there.
    return (
      (payError && getTransactionErrorMessage(payError, t)) ??
      t(RESOURCES.Error)
    );
  };

  const resolveCurrencyCode = (bankAccountID: number) => {
    if (paymentRequest) {
      return isApiCurrencyCode(paymentRequest.currencyCode)
        ? paymentRequest.currencyCode
        : undefined;
    }
    // A free-amount payment is denominated in the sender account's currency, as before; the
    // backend verifies the recipient's primary account matches.
    const currency = bankAccounts?.items?.find(
      (account) => account.bankAccountID === bankAccountID,
    )?.currency;
    return currency && isApiCurrencyCode(currency) ? currency : undefined;
  };

  const handleSubmit = async ({
    amount,
    bankAccountID,
    bankAccountEnvironment,
  }: PaymentSubmitData) => {
    const currencyCode = resolveCurrencyCode(bankAccountID);
    if (!currencyCode || !channel.id) {
      toast.error(t(RESOURCES.Error));
      return;
    }

    let payment: SendDirectPaymentResponse;
    try {
      payment = await sendDirectPayment({
        amount: { value: amount, currencyCode },
        recipientIdentityUserPublicID,
        senderBankAccountID: bankAccountID,
        channelId: channel.id,
      });
    } catch {
      // Shown by the drawer from the mutation's error state — stay open.
      return;
    }
    const {
      transactionPublicID,
      signingUrl,
      isServerAuthorized,
      requiresPispAuthentication,
    } = payment;

    // From here on the transaction exists and is waiting for the bank: the chat message is a
    // courtesy and must never block the redirect. A failed post (expired chat token, network)
    // is reported and the bank flow continues regardless.
    try {
      await channel.sendMessage({
        text: buildPaymentMessageText(t, {
          type: "direct_payment",
          amount,
          currencyCode,
        }),
        attachments: [
          {
            amount,
            currencyCode,
            transactionPublicID,
            type: "direct_payment",
            transactionStatus: TransactionStatus.Created,
          },
        ],
        // A reply to a multi-payer request lives in its thread only (show_in_channel defaults to
        // false): the request card is the one place that lists who paid.
        ...(paymentRequest ? { parent_id: paymentRequest.messageId } : {}),
      });
    } catch {
      toast.error(t(RESOURCES.MessageNotPosted));
    }
    onClose();

    if (signingUrl) {
      window.location.href = signingUrl;
      return;
    }
    if (requiresPispAuthentication) {
      fetchAndRedirectToPISPURL({
        environment: bankAccountEnvironment,
        transactionPublicID,
      });
      return;
    }
    if (isServerAuthorized) {
      // Authorized server-side (e.g. CZKT) — no bank redirect, show the outcome.
      navigate(`${appUrl("transaction-detail")}/${transactionPublicID}`);
      return;
    }
    toast.error(t(TRANSACTION_ERRORS.UnknownPaymentError));
  };

  return (
    <PaymentDrawerForm
      isOpen={isOpen}
      onClose={onClose}
      title={t(RESOURCES.Title)}
      bankAccountLabel={t(RESOURCES.BankAccount)}
      amountLabel={t(RESOURCES.Amount)}
      noteLabel={t(RESOURCES.Note)}
      notePlaceholder={t(RESOURCES.NotePlaceholder)}
      submitLabel={t(RESOURCES.Submit)}
      isPending={isPending}
      error={error}
      errorContent={getErrorContent()}
      fixedAmount={paymentRequest}
      onSubmit={handleSubmit}
    />
  );
};
